import { Backdrop, Box, CircularProgress, Typography } from "@mui/material";
import { ActionButton } from "components/ActionButton";
import FirstLineContainer from "components/common/FirstLineContainer";
import React, { useCallback, useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import PolicyForm from "./policyForm";
import ActiveHealthPlans from "../components/activeHealthPlans";
import api from "api";
import { useDispatch } from "react-redux";
import { setDialog } from "slices/appSlice";
import PolicyVersions from "../components/policyVersions";
import { formErrorStrfromObj } from "utils/shared";

function CreatePolicyConfig() {
  const { policyKey } = useParams();
  const { state } = useLocation();

  const {
    isAdmin = false,
    policyTypeOptions = [],
    policyNumber = "",
    policyTitle = "",
    versionText = "",
    versionHistory = [],
    isHighestVersion = false,
  } = state || {};

  const [loading, setLoading] = useState(true);
  const [policyDetails, setPolicyDetails] = useState(() => ({
    policyKey: "",
    policyNumber: "",
    policyTitle: "",
    versionText: "",
    policyTypeKey: null,
    policyType: "",
    quarter: "",
    year: "",
    mapToHealthPlan: true,
  }));
  const [activeHealthPlans, setActiveHealthPlans] = useState(new Map());
  const [nextQuarter, setNextQuarter] = useState("");
  const [nextYear, setNextYear] = useState("");
  const [versionFormatError, setVersionFormatError] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (policyKey) {
      fetchPolicyDetails();
    } else {
      setLoading(false);
    }
  }, [policyKey]);

  const fetchPolicyDetails = async () => {
    setLoading(true);
    try {
      const response =
        await api.PolicyConfiguration.getPolicyConfigDetails(policyKey);
      if (response.data?.policyConfigDto) {
        const policyDetailsFromDto = response.data.policyConfigDto;
        setPolicyDetails(policyDetailsFromDto);
        setNextQuarter(policyDetailsFromDto?.quarter || "");
        setNextYear(policyDetailsFromDto?.year || "");
      }
      if (response.data?.healthPlans) {
        setActiveHealthPlans(
          new Map(Object.entries(response.data.healthPlans))
        );
      }
    } catch (error) {
      dispatch(
        setDialog({
          message:
            formErrorStrfromObj(error) ||
            "Failed to retrieve the List of Policies",
          title: error.message || "Error",
        })
      );
      console.error("Error fetching policy configurations:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = useCallback(() => {
    navigate(-1);
  }, []);

  const isFormValid = Boolean(
    policyDetails.policyNumber?.trim() &&
      policyDetails.policyTitle?.trim() &&
      policyDetails.policyType?.trim() &&
      policyDetails.versionText?.trim() &&
      nextQuarter?.trim() &&
      nextYear?.toString().trim() &&
      !versionFormatError
  );

  const handleSave = useCallback(async () => {
    setLoading(true);
    try {
      const updatedPolicyDetails = {
        ...policyDetails,
        ...(nextQuarter && { quarter: nextQuarter }),
        ...(nextYear && { year: nextYear }),
      };
      if (policyKey) {
        await api.PolicyConfiguration.updatePolicyConfig(
          policyKey,
          updatedPolicyDetails
        );
        dispatch(
          setDialog({
            message: "Policy configuration updated successfully.",
            title: "Success",
          })
        );
      } else {
        // Create new policy configuration
        await api.PolicyConfiguration.createPolicyConfig(updatedPolicyDetails);
        dispatch(
          setDialog({
            message: "Policy configuration created successfully.",
            title: "Success",
          })
        );
      }
      // Update state for display (optional)
      setPolicyDetails(updatedPolicyDetails);
      setNextQuarter("");
      setNextYear("");
      navigate(-1);
    } catch (error) {
      dispatch(
        setDialog({
          message:
            formErrorStrfromObj(error) ||
            "Failed to save the policy configuration",
          title: error.message || "Error",
        })
      );
      console.error("Error saving policy configuration:", error);
    } finally {
      setLoading(false);
    }
  }, [policyKey, policyDetails, nextQuarter, nextYear, dispatch, navigate]);

  return (
    <>
      <FirstLineContainer>
        <Typography variant="h6" sx={{ textAlign: "left", flex: 1 }}>
          <Box
            data-testid="create-poliy-title"
            component="span"
            fontWeight="fontWeightBold"
          >
            Policy Configuration -{" "}
            {policyKey ? `Edit: ${policyNumber} ${policyTitle}` : "New"}
          </Box>
        </Typography>
      </FirstLineContainer>
      <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
        {/* ======= Policy Form ======= */}
        <Box
          data-testid="policy-form-holder"
          sx={{
            flex: 1,
            flexBasis: "45%",
            display: "flex",
            flexDirection: "column",
            ml: 2,
            mr: 5,
          }}
        >
          <PolicyForm
            policyKey={policyKey}
            policyDetails={policyDetails}
            setPolicyDetails={setPolicyDetails}
            policyTypeOptions={policyTypeOptions}
            nextQuarter={nextQuarter}
            setNextQuarter={setNextQuarter}
            nextYear={nextYear}
            setNextYear={setNextYear}
            setVersionFormatError={setVersionFormatError}
          />
        </Box>
        {policyKey && (
          <Box
            role="active-health-plans-and-policy-versions"
            sx={{
              flex: 1,
              flexBasis: "35%",
              gap: 2,
              display: "flex",
              flexDirection: "column",
              ml: 1,
            }}
          >
            <ActiveHealthPlans activeHealthPlans={activeHealthPlans} />
            <PolicyVersions policyVersions={versionHistory} />
          </Box>
        )}
        <Box
          sx={{ flex: 1, flexBasis: "20%", width: "100%", textAlign: "right" }}
        >
          <ActionButton
            data-testid="create-policy-cancel-button"
            variant="outlined"
            color="inherit"
            onClick={handleCancel}
          >
            CANCEL
          </ActionButton>
          {isAdmin && (isHighestVersion || !policyKey) && (
            <>
              <ActionButton
                data-testid="create-policy-save-button"
                variant="contained"
                color="primaryButton"
                onClick={handleSave}
                disableElevation
                disabled={!isFormValid}
              >
                SAVE
              </ActionButton>
            </>
          )}
        </Box>
      </Box>
      <Backdrop
        data-testid="create-policy-loading-indicator"
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
}
export default CreatePolicyConfig;
